import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const VEHICLE_EXCHANGE_ENGINE_RESOURCE = "vehicle-exchange-engine" as const;
const VEHICLE_EXCHANGE_ENGINE_API_PATH = `/v1/${VEHICLE_EXCHANGE_ENGINE_RESOURCE}` as const;
type VehicleExchangeEngineByUuidApiPath = "/v1/vehicle-exchange-engine/{uuid}";

type CreateBody = paths[typeof VEHICLE_EXCHANGE_ENGINE_API_PATH]["post"]["requestBody"]["content"]["application/json"];
type CreateResponse =
  paths[typeof VEHICLE_EXCHANGE_ENGINE_API_PATH]["post"]["responses"][201]["content"]["application/json"];
type UpdateBody = paths[VehicleExchangeEngineByUuidApiPath]["patch"]["requestBody"]["content"]["application/json"];
type UpdateResponse =
  paths[VehicleExchangeEngineByUuidApiPath]["patch"]["responses"][200]["content"]["application/json"];

function useInvalidateVehicleExchangeEngine() {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [VEHICLE_EXCHANGE_ENGINE_RESOURCE] }),
      queryClient.invalidateQueries({ queryKey: ["vehicle"] }),
    ]);
  };
}

export function useVehicleExchangeEngineCreateMutation() {
  const invalidate = useInvalidateVehicleExchangeEngine();

  return useMutation<CreateResponse, Error, CreateBody>({
    mutationFn: async (body) => {
      const response = await alphartisApiAxios.post<CreateResponse>(VEHICLE_EXCHANGE_ENGINE_API_PATH, body);
      return response.data;
    },
    onSuccess: invalidate,
  });
}

export function useVehicleExchangeEngineUpdateMutation() {
  const invalidate = useInvalidateVehicleExchangeEngine();

  return useMutation<UpdateResponse, Error, UpdateBody & { uuid: string }>({
    mutationFn: async ({ uuid, ...body }) => {
      const response = await alphartisApiAxios.patch<UpdateResponse>(
        `/v1/${VEHICLE_EXCHANGE_ENGINE_RESOURCE}/${uuid}`,
        body,
      );
      return response.data;
    },
    onSuccess: invalidate,
  });
}

export function useVehicleExchangeEngineDeleteMutation() {
  const invalidate = useInvalidateVehicleExchangeEngine();

  return useMutation<void, Error, { uuid: string }>({
    mutationFn: async ({ uuid }) => {
      await alphartisApiAxios.delete(`/v1/${VEHICLE_EXCHANGE_ENGINE_RESOURCE}/${uuid}`);
    },
    onSuccess: invalidate,
  });
}
